import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import EmptyState from '../../components/shell/EmptyState'
import DayCard, { type SecondaryDayEntry } from '../../components/agent/DayCard'
import HoursSummary from '../../components/agent/HoursSummary'
import StatusChip, { type RecordState } from '../../components/agent/StatusChip'
import {
  CATEGORY_FILL,
  dayAriaLabel,
  scheduledHours,
  totalScheduledHours,
  formatHours,
  type DayEntry,
} from '../../components/agent/scheduleDay'
import { supabase } from '../../lib/supabase'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { useRefetchOnFocus } from '../../lib/useRefetchOnFocus'
import { PAY_PERIOD, PAY_PERIOD_DATES } from './payPeriod19'
import { getMyPeriodState, getMyScheduleDays, getMyTeamSchedule, unscheduledDay, type TeamSchedule } from './agentScheduleStore'
import { getPendingDraft, type PendingDraft } from './pendingDraftStore'
import { getMyScheduleSubmissions, type MyScheduleSubmission } from './scheduleSubmissionStore'
import { getLeaveRequests, isDateApproved, leaveRequestDisplayState, type LeaveRequest } from './leaveRequestStore'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import ScheduleAcknowledgements from '../../components/agent/ScheduleAcknowledgements'
import { schedulingServiceEnabled } from '../../lib/features'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// The actual current date, not the fixed Sep 15 the placeholder pilot data
// is anchored to: this is what the "today" ring, in every view, and the
// month view's own Today button point at, independent of which period's
// data happens to be loaded. Read once at load, the same as the rest of
// this screen's "today" ever was; nothing here needs to tick live.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

// The current period always contains today (payPeriod19.ts is generated
// from the real clock), so this always finds it; the fallback to the first
// day only guards an index-based default pointing at nothing.
const TODAY_INDEX = Math.max(
  0,
  PAY_PERIOD_DATES.findIndex((entry) => entry.date === REAL_TODAY_ISO),
)

// The week and period ranges shown around the screen were hardcoded text
// while there was only ever one period to show; deriving them from the
// entries themselves instead is what makes those same labels correct once
// a Pay Period 20 draft can be the one on screen too.
function formatRange(days: DayEntry[]): string {
  const first = days[0]
  const last = days[days.length - 1]
  const firstLabel = `${first.month.slice(0, 3)} ${first.day}`
  const lastLabel = first.month === last.month ? `${last.day}` : `${last.month.slice(0, 3)} ${last.day}`
  return `${firstLabel}–${lastLabel}`
}

// "14:00" -> "2:00 PM", same derivation as AgentRequestHistoryPage.tsx's
// own formatTime: the leave form collects a 24-hour time input, but
// nothing else in this app shows one that way.
function formatTime(time: string): string {
  const [hourText, minute] = time.split(':')
  const hour = Number(hourText)
  const period = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${minute} ${period}`
}

// A leave request is a second entry on whichever dates it covers, per
// docs/product/scheduling-model.md's schedule entry record ("a day may
// hold more than one entry"), not a replacement for the day's own work
// status: an agent's calendar day can show both at once.
function leaveEntriesForDate(leaveRequests: LeaveRequest[], date: string): SecondaryDayEntry[] {
  return leaveRequests
    .filter((request) => request.startDate <= date && date <= request.endDate)
    // A request still pending, returned, or denied shows on every date it
    // covers regardless: modifiedDates only exists once a supervisor has
    // actually approved a subset of it. Once approved, isDateApproved is
    // the real test: an approve_modified decision excludes some of the
    // requested dates, and this date should read as ordinary rather than
    // still granted leave the supervisor never actually approved.
    .filter((request) => request.state !== 'approved' || isDateApproved(request, date))
    .map((request) => ({
      id: request.id,
      title: request.leaveType,
      detail: request.fullDay
        ? 'Full day'
        : `Partial day, ${formatTime(request.startTime ?? '00:00')}–${formatTime(request.endTime ?? '00:00')}`,
      state: leaveRequestDisplayState(request.state),
    }))
}

type CalendarView = 'week1' | 'week2' | 'period' | 'month' | 'day' | 'team'

const VIEW_LABEL: Record<CalendarView, string> = {
  week1: 'Week 1',
  week2: 'Week 2',
  period: 'Period',
  month: 'Month',
  day: 'Day',
  team: 'Team',
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  )
}

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

function ViewSwitcher({ view, onChange }: { view: CalendarView; onChange: (view: CalendarView) => void }) {
  return (
    <div role="group" aria-label="Calendar view" className="flex gap-0.5 rounded-control bg-surface-2 p-0.5">
      {(Object.keys(VIEW_LABEL) as CalendarView[]).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={view === option}
          onClick={() => onChange(option)}
          className={`flex min-h-11 flex-1 items-center justify-center rounded-[5px] px-1 text-caption font-bold ${
            view === option ? 'bg-canvas text-ink shadow-sm' : 'text-ink-subtle'
          }`}
        >
          {VIEW_LABEL[option]}
        </button>
      ))}
    </div>
  )
}

// Period-crossing navigation has no adjacent period's data yet (the
// schedule schema is not built): rather than show arrows that can never do
// anything, Week 1, Week 2, and Period show only the range itself.
function PeriodNav({ rangeLabel, onToday }: { rangeLabel: string; onToday: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="px-1 text-small font-bold [font-variant-numeric:tabular-nums]">{rangeLabel}</span>
      <button
        type="button"
        onClick={onToday}
        className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3.5 text-small font-bold text-ink-muted"
      >
        Today
      </button>
    </div>
  )
}

// Unlike PeriodNav, whose prev/next stay disabled for want of an adjacent
// period's real data, the month grid already renders any month gracefully
// (unscheduled placeholders where nothing is loaded), so moving between
// months needs no data it does not already have.
function MonthNav({
  label,
  onPrev,
  onNext,
  onToday,
}: {
  label: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1 text-small font-bold">
        <button
          type="button"
          onClick={onPrev}
          aria-label="Previous month"
          className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted"
        >
          <ChevronLeftIcon />
        </button>
        {/* A fixed width, not just the three-letter abbreviation, is what
            actually holds the arrows still: letters are not equal width in
            this typeface, so "Sep" and "Oct" alone still nudged them by a
            couple of pixels every step. */}
        <span className="inline-block w-24 text-center [font-variant-numeric:tabular-nums]">{label}</span>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next month"
          className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted"
        >
          <ChevronRightIcon />
        </button>
      </div>
      <button
        type="button"
        onClick={onToday}
        className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3.5 text-small font-bold text-ink-muted"
      >
        Today
      </button>
    </div>
  )
}

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function monthDateFor(year: number, monthIndex: number, day: number, monthName: string) {
  const date = new Date(year, monthIndex, day)
  return {
    date: `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    weekday: WEEKDAY_NAMES[date.getDay()],
    day,
    month: monthName,
  }
}

// A placeholder entry for a date no pay period covers yet, so the month
// grid has something real to show and tap into instead of leaving the date
// blank. `unscheduled` is a UI-only category, not one of the status codes
// in docs/product/scheduling-model.md; see scheduleDay.ts.
function unscheduledEntry(year: number, monthIndex: number, day: number, monthName: string): DayEntry {
  return { ...monthDateFor(year, monthIndex, day, monthName), category: 'unscheduled', label: 'Nothing scheduled' }
}

// A leave request can reach up to REQUEST_HORIZON_PAY_PERIODS pay
// periods out (requestHorizon.ts), well past Pay Period 19's own loaded
// DAYS, so a date the grid would otherwise call unscheduled needs its
// own real entry here too, the same as a real schedule day already gets
// from DAYS. Pending, returned, and denied requests stay unscheduled at
// the grid level: DaySheet's own leaveEntries already surfaces one
// regardless of state when the date is tapped, but only an approved
// request is real enough to color the month at a glance.
function approvedLeaveEntry(request: LeaveRequest, year: number, monthIndex: number, day: number, monthName: string): DayEntry {
  return {
    ...monthDateFor(year, monthIndex, day, monthName),
    category: 'leave',
    label: request.leaveType,
    state: leaveRequestDisplayState(request.state),
  }
}

function MonthGrid({
  year,
  monthIndex,
  onPickDay,
  days,
  draftDays,
  draftApproved,
  leaveRequests,
}: {
  year: number
  monthIndex: number
  onPickDay: (entry: DayEntry, trigger: HTMLButtonElement) => void
  // The agent's own current-period days, real entries or unscheduled.
  days: DayEntry[]
  // Pay Period 19 and a Pay Period 20 draft never overlap in date, so this
  // grid shows both at once, whatever month is on screen, rather than
  // needing a toggle the way the period-scoped views below do.
  draftDays: DayEntry[]
  // Whether a supervisor has already approved the real submission behind
  // draftDays: still shown (it may be the only entry a next-period date
  // has), but no longer marked as a draft still awaiting a decision.
  draftApproved: boolean
  leaveRequests: LeaveRequest[]
}) {
  const { format } = useTimeFormat()
  const monthName = MONTH_NAMES[monthIndex]
  const monthPrefix = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  const firstWeekday = new Date(year, monthIndex, 1).getDay()
  const draftDates = new Set(draftDays.map((entry) => entry.date))
  // Every day of the month is tappable, whether or not a pay period covers
  // it; the ones that are get a real schedule entry and its category color,
  // and the rest fall back to a plain, unstyled number that still opens the
  // day sheet, reporting nothing scheduled. Matched by the full date, not
  // just the day-of-month: with more than one month ever on screen, a
  // day-of-month match alone would pull September's 8th into every other
  // month's 8th too.
  const cells: (DayEntry | null)[] = [...Array(firstWeekday).fill(null)]
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${monthPrefix}-${String(day).padStart(2, '0')}`
    const real = days.find((entry) => entry.date === iso && entry.category !== 'unscheduled')
    const draft = draftDays.find((entry) => entry.date === iso)
    const approvedLeave = leaveRequests.find(
      (request) => request.startDate <= iso && iso <= request.endDate && isDateApproved(request, iso),
    )
    cells.push(
      real ??
        draft ??
        (approvedLeave ? approvedLeaveEntry(approvedLeave, year, monthIndex, day, monthName) : unscheduledEntry(year, monthIndex, day, monthName)),
    )
  }

  // Whichever of Pay Period 19, the draft, or an approved leave actually
  // has a day in the month on screen, so the caption only claims what is
  // true of it instead of always naming September.
  const showsCurrentPeriod = PAY_PERIOD_DATES.some((entry) => entry.date.startsWith(monthPrefix))
  const showsDraft = !draftApproved && draftDays.some((entry) => entry.date.startsWith(monthPrefix))
  const showsApprovedLeave = cells.some((entry) => entry?.category === 'leave')

  // Seven fixed 44px columns, matching the project's minimum touch target,
  // do not fit a 320px viewport with any padding to spare: the same "wide
  // tables" pattern docs/design/style-guide.md prescribes for the planning
  // board applies here, a labelled region that scrolls sideways on its own
  // rather than shrinking every cell to fit. There is room to spare above
  // the `sm` breakpoint, where the grid goes back to filling the width it
  // is given instead of scrolling.
  return (
    <div>
      <div role="region" aria-label={`${monthName} ${year} calendar`} tabIndex={0} className="overflow-x-auto">
        <div className="w-max sm:w-full">
          <div className="grid grid-cols-[repeat(7,2.75rem)] gap-1 text-center text-caption font-bold text-ink-subtle sm:grid-cols-7">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((label) => (
              <div key={label} className="py-1">
                {label}
              </div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-[repeat(7,2.75rem)] gap-1 sm:grid-cols-7">
            {cells.map((entry, index) => {
              if (!entry) return <div key={`pad-${index}`} aria-hidden="true" />

              const isToday = entry.date === REAL_TODAY_ISO

              if (entry.category === 'unscheduled') {
                return (
                  <button
                    key={entry.date}
                    type="button"
                    onClick={(event) => onPickDay(entry, event.currentTarget)}
                    aria-label={isToday ? `${dayAriaLabel(entry, format)}, today` : dayAriaLabel(entry, format)}
                    className={`flex size-11 items-center justify-center rounded-control text-small font-semibold text-ink-subtle sm:size-auto sm:aspect-square sm:w-full ${
                      isToday ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    {entry.day}
                  </button>
                )
              }

              const isDraft = !draftApproved && draftDates.has(entry.date)
              // Both suffixes are informational, not visual-only: the ring
              // and the dashed border each need a word in the name too, or
              // a screen-reader user never learns what the border and the
              // ring are announcing.
              const nameSuffixes = [isDraft && 'draft', isToday && 'today'].filter(Boolean).join(', ')
              return (
                <button
                  key={entry.date}
                  type="button"
                  onClick={(event) => onPickDay(entry, event.currentTarget)}
                  aria-label={nameSuffixes ? `${dayAriaLabel(entry, format)}, ${nameSuffixes}` : dayAriaLabel(entry, format)}
                  className={`flex size-11 flex-col items-center justify-center gap-0.5 rounded-control text-small font-bold sm:size-auto sm:aspect-square sm:w-full ${
                    CATEGORY_FILL[entry.category]
                  } ${isDraft ? 'border-2 border-dashed border-primary' : 'border border-line'} ${
                    isToday ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  {entry.day}
                </button>
              )
            })}
          </div>
        </div>
      </div>
      <p className="mt-3 text-caption text-ink-subtle">
        {showsCurrentPeriod && (
          <>
            {PAY_PERIOD.label}&apos;s dates, {PAY_PERIOD.range}, are shown in color.{' '}
          </>
        )}
        {showsDraft && 'A dashed border marks your saved draft. '}
        {showsApprovedLeave && 'An approved leave request is shown in its own color, even outside a loaded pay period. '}
        Tap any day for its detail.
      </p>
    </div>
  )
}

// A day picked from the month grid opens here rather than switching the
// whole screen to the Day view, so glancing at one date does not lose the
// grid underneath it. The rest of the page goes inert while this is open:
// it is a true modal, not a panel beside still-reachable content.
function DaySheet({
  entry,
  onClose,
  closeButtonRef,
  leaveEntries,
}: {
  entry: DayEntry
  onClose: () => void
  leaveEntries: SecondaryDayEntry[]
  closeButtonRef: React.RefObject<HTMLButtonElement | null>
}) {
  const { format } = useTimeFormat()
  // Starts translated fully off-screen and opacity-0, then flips to its
  // resting state a frame later so the transition classes below actually
  // animate instead of snapping straight to the open state. Deferred to a
  // frame, not called straight from the effect body, so the browser has
  // committed the starting position first.
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className={`absolute inset-0 bg-ink/40 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={dayAriaLabel(entry, format)}
        className={`relative flex min-h-[45vh] w-full max-w-md flex-col rounded-t-surface border-t border-x border-line bg-canvas p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg transition-transform duration-300 ease-out ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div aria-hidden="true" className="mx-auto mb-3 h-1 w-10 flex-none rounded-full bg-line-strong" />
        <div className="mb-2 flex flex-none justify-end">
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex size-11 items-center justify-center rounded-control text-ink-muted"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="flex flex-1 flex-col justify-center">
          <DayCard entry={entry} expanded today={entry.date === REAL_TODAY_ISO} leaveEntries={leaveEntries} />
        </div>
      </div>
    </div>
  )
}

export default function AgentCalendarPage() {
  const { format } = useTimeFormat()
  const identity = useProfileIdentity()
  const [view, setView] = useState<CalendarView>(TODAY_INDEX < 7 ? 'week1' : 'week2')
  const [dayIndex, setDayIndex] = useState(TODAY_INDEX)
  const [peekEntry, setPeekEntry] = useState<DayEntry | null>(null)
  const peekTriggerRef = useRef<HTMLButtonElement | null>(null)
  const peekCloseRef = useRef<HTMLButtonElement | null>(null)
  // A saved draft loading in a tick after mount is a low-stakes reveal, not
  // a gate: without one, nothing on this screen changes, so there is no
  // loading state to hide behind the way the copy screen needs one.
  const [savedDraft, setSavedDraft] = useState<PendingDraft | null>(null)
  // The real submission behind savedDraft, once a supervisor has decided
  // it: savedDraft itself only ever moves from 'draft' to 'submitted'
  // locally (pendingDraftStore.ts), so it still reads as a submitted
  // draft on this screen even once it is actually approved. Read
  // alongside savedDraft so the month grid below can tell the two apart.
  const [submissions, setSubmissions] = useState<MyScheduleSubmission[]>([])
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([])
  // The agent's own real current period: every day an entry or an
  // unscheduled placeholder, the period's own state, and their team as it
  // was last published. Starts as an unscheduled period so the views render
  // before the read lands.
  const [days, setDays] = useState<DayEntry[]>(() => PAY_PERIOD_DATES.map(unscheduledDay))
  const [periodState, setPeriodState] = useState<RecordState | null>(null)
  const [teamSchedule, setTeamSchedule] = useState<TeamSchedule | null>(null)
  // Which period's days the period-scoped views (Week 1, Week 2, Period,
  // Day) show. The month grid ignores this: Pay Period 19 and a draft never
  // share a date, so it shows both at once instead of needing a toggle.
  const [activePeriod, setActivePeriod] = useState<'current' | 'draft'>('current')
  // The month grid's own, independent position: starts on the real current
  // month rather than a fixed one, and moves freely since it never needs
  // data beyond what it already renders gracefully without.
  const [viewedMonth, setViewedMonth] = useState({ year: now.getFullYear(), monthIndex: now.getMonth() })

  // Reloads the agent's own real leave requests and local draft: called
  // on mount, and again whenever the tab regains focus
  // (useRefetchOnFocus below), the same reason AgentDashboardPage.tsx's
  // own loadRealData does. Kicks the read off rather than awaiting it
  // directly: every setState call still happens inside the `.then`
  // callback, not this function's own body.
  function loadRealData(): void {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id ?? null
      const draft = id ? getPendingDraft(id) : null
      if (draft) setSavedDraft(draft)
      if (id) {
        const requests = await getLeaveRequests(id)
        setLeaveRequests(requests)
        const loadedDays = await getMyScheduleDays(id, PAY_PERIOD_DATES)
        setDays(loadedDays)
        setPeriodState(await getMyPeriodState(id, PAY_PERIOD_DATES[0].date, loadedDays))
        setTeamSchedule(await getMyTeamSchedule(id, PAY_PERIOD_DATES[0].date))
        setSubmissions(await getMyScheduleSubmissions(id))
      }
    })
  }

  useEffect(() => {
    loadRealData()
  }, [])

  useRefetchOnFocus(loadRealData)

  // Moves focus into the sheet's close button on open, and back to the day
  // that opened it on close. A synchronous focus call in the click handler
  // that opens it would run before the rest of the page has actually gone
  // inert, so this waits for the commit like AdminDashboardPage's drawer
  // focus does.
  useEffect(() => {
    if (peekEntry) {
      peekCloseRef.current?.focus()
    } else {
      peekTriggerRef.current?.focus()
    }
  }, [peekEntry])

  function openDaySheet(entry: DayEntry, trigger: HTMLButtonElement) {
    peekTriggerRef.current = trigger
    setPeekEntry(entry)
  }

  function closeDaySheet() {
    setPeekEntry(null)
  }

  function goToToday() {
    // Today's actual date only ever falls in the current period; jumping
    // there from the draft would otherwise land on a day index with
    // nothing to show.
    setActivePeriod('current')
    setDayIndex(TODAY_INDEX)
    setView((current) => (current === 'week1' ? 'week2' : current))
  }

  function switchPeriod(period: 'current' | 'draft') {
    setActivePeriod(period)
    setDayIndex(period === 'current' ? TODAY_INDEX : 0)
  }

  function goToPrevMonth() {
    setViewedMonth((current) =>
      current.monthIndex === 0
        ? { year: current.year - 1, monthIndex: 11 }
        : { year: current.year, monthIndex: current.monthIndex - 1 },
    )
  }

  function goToNextMonth() {
    setViewedMonth((current) =>
      current.monthIndex === 11
        ? { year: current.year + 1, monthIndex: 0 }
        : { year: current.year, monthIndex: current.monthIndex + 1 },
    )
  }

  function goToCurrentMonth() {
    setViewedMonth({ year: now.getFullYear(), monthIndex: now.getMonth() })
  }

  // A draft for the current pay period is that same period's own schedule,
  // already what the current-period days show, so it is not a second period
  // to switch to: the toggle only offers a draft for a different one.
  const draftIsAnotherPeriod = savedDraft !== null && savedDraft.periodStart !== PAY_PERIOD_DATES[0].date
  const activeDays = activePeriod === 'draft' && savedDraft && draftIsAnotherPeriod ? savedDraft.days : days

  // Once a supervisor approves the real submission behind it, savedDraft
  // is no longer "your saved draft" the month grid's own dashed border
  // means: it is a decided record now, the same as any other approved
  // day. Matched by periodStart, the only field both share.
  const savedDraftApproved =
    savedDraft !== null &&
    submissions.some((submission) => submission.periodStart === savedDraft.periodStart && submission.state === 'approved')

  // The period toggle only ever applies to the four period-scoped views:
  // Month renders Pay Period 19 and a draft together rather than choosing
  // between them, and Team always reads the real Pay Period 19 roster, a
  // draft being this one agent's own unsubmitted schedule rather than
  // anyone's actual assignment. Showing the toggle's period here regardless
  // would leave the header naming a pay period, or a Draft state, that the
  // view on screen has nothing to do with.
  const activeMeta: { label: string; range: string; state: RecordState | null } =
    view !== 'month' && view !== 'team' && activePeriod === 'draft' && savedDraft && draftIsAnotherPeriod
      ? { label: savedDraft.periodLabel, range: formatRange(savedDraft.days), state: savedDraft.state }
      : { label: PAY_PERIOD.label, range: PAY_PERIOD.range, state: periodState }

  const week1 = activeDays.slice(0, 7)
  const week2 = activeDays.slice(7, 14)
  const selectedDay = activeDays[dayIndex]

  // The team roster always reflects the real Pay Period 19 calendar, never
  // a personal draft: a draft is only ever this one agent's own unsubmitted
  // schedule, not a change to what a teammate is actually working.
  const rosterDay = days[Math.min(dayIndex, days.length - 1)]

  return (
    <>
      <div
        inert={peekEntry ? true : undefined}
        className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0"
      >
        <a
          href="#calendar-main"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
        >
          Skip to main content
        </a>

        <AppHeader initials={identity?.initials ?? ''} />

        <main id="calendar-main" tabIndex={0} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 lg:gap-6 lg:p-8">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 lg:gap-6">
            <Link
              to="/agent"
              className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
            >
              <ArrowLeftIcon />
              Dashboard
            </Link>

            <div className="flex items-baseline justify-between gap-3">
              <div>
                <h1 className="text-h3 font-extrabold lg:text-h2">My Schedule</h1>
                <p className="mt-0.5 text-small text-ink-muted">
                  {activeMeta.label} &middot; {activeMeta.range}
                </p>
              </div>
              {activeMeta.state && <StatusChip state={activeMeta.state} />}
            </div>

            {schedulingServiceEnabled() && <ScheduleAcknowledgements />}

            {savedDraft && draftIsAnotherPeriod && view !== 'month' && view !== 'team' && (
              <div role="group" aria-label="Which pay period" className="flex gap-0.5 rounded-control bg-surface-2 p-0.5">
                <button
                  type="button"
                  aria-pressed={activePeriod === 'current'}
                  onClick={() => switchPeriod('current')}
                  className={`flex min-h-11 flex-1 items-center justify-center rounded-[5px] px-2 text-caption font-bold ${
                    activePeriod === 'current' ? 'bg-canvas text-ink shadow-sm' : 'text-ink-subtle'
                  }`}
                >
                  {PAY_PERIOD.label}
                </button>
                <button
                  type="button"
                  aria-pressed={activePeriod === 'draft'}
                  onClick={() => switchPeriod('draft')}
                  className={`flex min-h-11 flex-1 items-center justify-center rounded-[5px] px-2 text-caption font-bold ${
                    activePeriod === 'draft' ? 'bg-canvas text-ink shadow-sm' : 'text-ink-subtle'
                  }`}
                >
                  {savedDraft.periodLabel} &middot; {savedDraft.state === 'submitted' ? 'Submitted' : 'Draft'}
                </button>
              </div>
            )}

            <ViewSwitcher view={view} onChange={setView} />

            {view === 'week1' && (
              <>
                <PeriodNav rangeLabel={formatRange(week1)} onToday={goToToday} />
                <p className="text-small text-ink-muted">Scheduled: {formatHours(totalScheduledHours(week1))}</p>
                <div className="flex flex-col gap-2">
                  {week1.map((entry) => (
                    <DayCard
                      key={entry.date}
                      entry={entry}
                      today={entry.date === REAL_TODAY_ISO}
                      leaveEntries={leaveEntriesForDate(leaveRequests, entry.date)}
                    />
                  ))}
                </div>
              </>
            )}

            {view === 'week2' && (
              <>
                <PeriodNav rangeLabel={formatRange(week2)} onToday={goToToday} />
                <p className="text-small text-ink-muted">Scheduled: {formatHours(totalScheduledHours(week2))}</p>
                <div className="flex flex-col gap-2">
                  {week2.map((entry) => (
                    <DayCard
                      key={entry.date}
                      entry={entry}
                      today={entry.date === REAL_TODAY_ISO}
                      leaveEntries={leaveEntriesForDate(leaveRequests, entry.date)}
                    />
                  ))}
                </div>
              </>
            )}

            {view === 'period' && (
              <>
                <PeriodNav rangeLabel={activeMeta.range} onToday={goToToday} />
                <HoursSummary
                  rows={[
                    { label: 'Week 1', hours: totalScheduledHours(week1) },
                    { label: 'Week 2', hours: totalScheduledHours(week2) },
                  ]}
                  total={totalScheduledHours(activeDays)}
                  compareToTourOfDuty
                />
                <div className="flex flex-col gap-4">
                  <div>
                    <h2 className="mb-2 text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Week 1 &middot; {formatRange(week1)}
                    </h2>
                    <div className="flex flex-col gap-2">
                      {week1.map((entry) => (
                        <DayCard
                          key={entry.date}
                          entry={entry}
                          today={entry.date === REAL_TODAY_ISO}
                          leaveEntries={leaveEntriesForDate(leaveRequests, entry.date)}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <h2 className="mb-2 text-caption font-bold tracking-wide text-ink-subtle uppercase">
                      Week 2 &middot; {formatRange(week2)}
                    </h2>
                    <div className="flex flex-col gap-2">
                      {week2.map((entry) => (
                        <DayCard
                          key={entry.date}
                          entry={entry}
                          today={entry.date === REAL_TODAY_ISO}
                          leaveEntries={leaveEntriesForDate(leaveRequests, entry.date)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {view === 'month' && (
              <>
                <MonthNav
                  // Abbreviated so the label's width stays close to
                  // constant as the month changes; "September" next to
                  // "May" shifted the arrows flanking it on every step.
                  label={`${MONTH_NAMES[viewedMonth.monthIndex].slice(0, 3)} ${viewedMonth.year}`}
                  onPrev={goToPrevMonth}
                  onNext={goToNextMonth}
                  onToday={goToCurrentMonth}
                />
                <MonthGrid
                  year={viewedMonth.year}
                  monthIndex={viewedMonth.monthIndex}
                  onPickDay={openDaySheet}
                  days={days}
                  draftDays={savedDraft?.days ?? []}
                  draftApproved={savedDraftApproved}
                  leaveRequests={leaveRequests}
                />
              </>
            )}

            {view === 'day' && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setDayIndex((index) => Math.max(0, index - 1))}
                      disabled={dayIndex === 0}
                      aria-label="Previous day"
                      className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
                    >
                      <ChevronLeftIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDayIndex((index) => Math.min(activeDays.length - 1, index + 1))}
                      disabled={dayIndex === activeDays.length - 1}
                      aria-label="Next day"
                      className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
                    >
                      <ChevronRightIcon />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={goToToday}
                    className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3.5 text-small font-bold text-ink-muted"
                  >
                    Today
                  </button>
                </div>
                <DayCard
                  entry={selectedDay}
                  expanded
                  today={selectedDay.date === REAL_TODAY_ISO}
                  leaveEntries={leaveEntriesForDate(leaveRequests, selectedDay.date)}
                />
                {scheduledHours(selectedDay) > 0 && (
                  <p className="text-center text-small text-ink-muted">
                    Scheduled: {formatHours(scheduledHours(selectedDay))}
                  </p>
                )}
              </>
            )}

            {view === 'team' && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setDayIndex((index) => Math.max(0, index - 1))}
                      disabled={dayIndex === 0}
                      aria-label="Previous day"
                      className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
                    >
                      <ChevronLeftIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDayIndex((index) => Math.min(days.length - 1, index + 1))}
                      disabled={dayIndex === days.length - 1}
                      aria-label="Next day"
                      className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
                    >
                      <ChevronRightIcon />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={goToToday}
                    className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3.5 text-small font-bold text-ink-muted"
                  >
                    Today
                  </button>
                </div>

                <div>
                  <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">
                    {rosterDay.weekday}, {rosterDay.month} {rosterDay.day}
                    {rosterDay.date === REAL_TODAY_ISO ? ' · Today' : ''}
                  </p>
                  {teamSchedule && <p className="mt-0.5 text-small text-ink-muted">{teamSchedule.teamName}</p>}
                </div>

                {teamSchedule && teamSchedule.members.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {teamSchedule.members.map((member) => {
                      const entry = member.days.find((day) => day.date === rosterDay.date)
                      if (!entry) return null
                      return (
                        <div
                          key={member.id}
                          className={`flex items-center justify-between gap-3 rounded-surface border border-line p-3 ${CATEGORY_FILL[entry.category]}`}
                        >
                          <p className="text-small font-bold">{member.name}</p>
                          <p className="text-small text-ink-muted">{formatScheduleLabel(entry.label, format)}</p>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <EmptyState>Your team has no published schedule for this pay period yet.</EmptyState>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      {peekEntry && (
        <DaySheet
          entry={peekEntry}
          onClose={closeDaySheet}
          closeButtonRef={peekCloseRef}
          leaveEntries={leaveEntriesForDate(leaveRequests, peekEntry.date)}
        />
      )}
    </>
  )
}
